import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Umbrella, HeartHandshake, ArrowRight, CheckCircle2 } from "lucide-react";

const Landing = () => {
  const navigate = useNavigate();

  const tiers = [
    {
      name: "Essential",
      price: "$9.99",
      coverage: "Up to $150 per event, $250 per 30 days",
      features: ["Basic protection", "Email support", "Same-day review"]
    },
    {
      name: "Standard",
      price: "$19.99",
      coverage: "Up to $300 per event, $500 per 30 days",
      features: ["Enhanced protection", "Priority support", "Fast-track review", "Flexible repayment"],
      featured: true
    },
    {
      name: "Plus",
      price: "$39.99",
      coverage: "Up to $600 per event, $1000 per 30 days",
      features: ["Maximum protection", "24/7 support", "Instant review", "Custom repayment plans"]
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Umbrella className="h-8 w-8 text-primary" />
            <span className="text-2xl font-heading font-bold text-primary">DueDay</span>
          </div>
          <div className="flex items-center gap-4">
            <Button 
              data-testid="nav-login-btn"
              variant="ghost" 
              onClick={() => navigate('/login')}
              className="rounded-full"
            >
              Sign In
            </Button>
            <Button 
              data-testid="nav-signup-btn"
              onClick={() => navigate('/signup')}
              className="btn-primary"
            >
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 flex flex-col gap-8">
              <h1 className="text-5xl md:text-6xl font-heading font-bold tracking-tight text-foreground leading-tight">
                Your Safety Net for <span className="text-primary">Essential Bills</span>
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                Small monthly subscription that helps you pay essential bills on time, avoiding late fees and service disconnections. Peace of mind when unexpected expenses arise.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  data-testid="hero-get-started-btn"
                  onClick={() => navigate('/signup')} 
                  className="btn-primary text-xl px-12 py-7"
                >
                  Join DueDay <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  data-testid="hero-learn-more-btn"
                  variant="outline" 
                  className="btn-outline text-xl px-12 py-7"
                  onClick={() => document.getElementById('tiers').scrollIntoView({ behavior: 'smooth' })}
                >
                  See Plans
                </Button>
              </div>
            </div>
            <div className="lg:col-span-5 relative h-full min-h-[500px]">
              <div className="rounded-3xl overflow-hidden shadow-2xl h-full">
                <img 
                  src="https://images.pexels.com/photos/6932280/pexels-photo-6932280.jpeg" 
                  alt="Happy person experiencing financial peace"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 bg-secondary/30">
        <div className="container mx-auto">
          <h2 className="text-4xl md:text-5xl font-heading font-semibold text-center mb-16">
            How DueDay Protects You
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="card-feature" data-testid="feature-protection">
              <ShieldCheck className="h-12 w-12 text-primary mb-4" />
              <h3 className="text-2xl font-heading font-semibold mb-3">Late Fee Protection</h3>
              <p className="text-muted-foreground leading-relaxed">
                Avoid costly late fees and service disconnections when unexpected expenses hit. We've got your back when you need it most.
              </p>
            </div>
            <div className="card-feature" data-testid="feature-coverage">
              <Umbrella className="h-12 w-12 text-primary mb-4" />
              <h3 className="text-2xl font-heading font-semibold mb-3">Essential Bill Coverage</h3>
              <p className="text-muted-foreground leading-relaxed">
                Keep the lights on, stay connected, and maintain your essential services without the stress.
              </p>
            </div>
            <div className="card-feature" data-testid="feature-support">
              <HeartHandshake className="h-12 w-12 text-primary mb-4" />
              <h3 className="text-2xl font-heading font-semibold mb-3">Human-First Support</h3>
              <p className="text-muted-foreground leading-relaxed">
                Real people reviewing your requests. Fair, transparent, and always on your side.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="tiers" className="py-24 px-6">
        <div className="container mx-auto">
          <h2 className="text-4xl md:text-5xl font-heading font-semibold text-center mb-4">
            Choose Your Protection Level
          </h2>
          <p className="text-xl text-muted-foreground text-center mb-16">
            All plans include human review, flexible repayment, and member-friendly terms.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {tiers.map((tier, index) => (
              <div 
                key={tier.name}
                data-testid={`tier-${tier.name.toLowerCase()}`}
                className={`card-pricing ${tier.featured ? 'ring-2 ring-primary scale-105' : ''}`}
              >
                {tier.featured && (
                  <div className="absolute top-0 right-0 bg-accent text-accent-foreground px-4 py-1 text-sm font-medium rounded-bl-lg">
                    Most Popular
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-heading font-bold mb-2">{tier.name}</h3>
                  <div className="text-5xl font-heading font-bold text-primary mb-2">
                    {tier.price}<span className="text-2xl text-muted-foreground">/mo</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{tier.coverage}</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  data-testid={`select-${tier.name.toLowerCase()}-btn`}
                  onClick={() => navigate('/signup')}
                  className="btn-primary w-full"
                >
                  Get Started
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-primary text-primary-foreground">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
            Ready to Protect Your Peace of Mind?
          </h2>
          <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
            Join thousands who sleep better knowing their essential bills are covered.
          </p>
          <Button 
            data-testid="footer-cta-btn"
            onClick={() => navigate('/signup')}
            className="bg-white text-primary hover:bg-white/90 rounded-full px-12 py-7 text-xl font-medium shadow-lg"
          >
            Join DueDay Today
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-muted/30">
        <div className="container mx-auto text-center text-muted-foreground">
          <p className="mb-2">© 2025 DueDay. All rights reserved.</p>
          <p className="text-sm">
            DueDay membership charges are handled securely by Stripe. Coverage is subject to admin review and available pool.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
